import { ref } from 'vue'
import { isLeagueId, type LeagueId, type PackId } from '@shared/index'
import { api } from '../api/http'
import { useAuthStore } from '../stores/auth'
import { useCollectionStore } from '../stores/collection'

export type SeasonNoticeItem =
  | { kind: 'final'; league: LeagueId; packs: number }
  | { kind: 'start' }

/** Unseen season prizes. Empty hides the toast. */
export const seasonNoticeItems = ref<SeasonNoticeItem[]>([])

let inflight: Promise<void> | null = null
let claimed = false
let acked = false

/**
 * Claims the open season's set pack when it is still owed, then arms the toast.
 * A miss stays unclaimed so a later visit can try again.
 */
export function pullSeasonNotice(): Promise<void> {
  if (claimed) return Promise.resolve()
  if (inflight) return inflight
  inflight = loadNotice().finally(() => {
    inflight = null
  })
  return inflight
}

function isNoticeItem(raw: unknown): raw is SeasonNoticeItem {
  if (!raw || typeof raw !== 'object') return false
  const row = raw as { kind?: unknown; league?: unknown; packs?: unknown }
  if (row.kind === 'start') return true
  return (
    row.kind === 'final' &&
    isLeagueId(row.league) &&
    typeof row.packs === 'number' &&
    row.packs > 0
  )
}

async function loadNotice(): Promise<void> {
  const token = useAuthStore().token
  if (!token) return
  const collection = useCollectionStore()
  const epochAtFetch = collection.mutationEpoch()
  try {
    const body = await api<{ items?: unknown; packs?: Record<PackId, number> }>(
      '/rating/season-notice',
      { method: 'POST', token },
    )
    const items = Array.isArray(body.items) ? body.items.filter(isNoticeItem) : []
    if (items.length === 0) return
    if (collection.mutationEpoch() !== epochAtFetch) return
    if (!body.packs) return
    claimed = true
    acked = false
    collection.applyPacks(body.packs)
    seasonNoticeItems.value = items
  } catch {
    /* A later visit can try again. */
  }
}

/** Marks the notice read. Safe to call more than once. */
export async function ackSeasonNotice(): Promise<void> {
  if (acked) return
  acked = true
  const token = useAuthStore().token
  if (!token) return
  try {
    await api('/rating/season-notice/ack', { method: 'POST', token })
  } catch {
    acked = false
  }
}
